import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme';
import { StyledButton } from '@cp/ds/src/components/styled/styledButton';
import { ButtonProps } from '@cp/ds/src/components/button';
import { Typography } from '@cp/ds/src/components/typography';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import capitalize from 'lodash/capitalize';
import React from 'react';
import { AnnounceHintWrapper } from '@cp/ds/src/components/announceHintWrapper';
import { CareerId } from '@common/const/catalog/career';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { careerIdToLayoutData } from '../lib';
import { StatsLabel } from './statsLabel';

export interface CareerTileProps extends ButtonProps {
  careerId: CareerId;
  label: string;
  catalogStats?: {
    count: number;
    catalogType: CatalogItemType;
  };
}
export const CareerTile: React.FC<CareerTileProps> = ({ careerId, label, sx, onClick, catalogStats, disabled }) => {
  const theme = useTheme();
  const dictItem = careerIdToLayoutData[careerId];

  if (!dictItem) {
    return null;
  }

  const Icon = dictItem.iconComponent;

  return (
    <AnnounceHintWrapper>
      {({ setAnnounceMessageVisible }) => (
        <StyledButton
          disabled={disabled}
          onClick={disabled ? undefined : onClick ?? (() => setAnnounceMessageVisible(true))}
          sx={{
            width: '100%',
            display: 'flex',
            background: theme.palette.white,
            alignItems: 'center',
            flexDirection: 'column',
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            p: { xs: theme.spacing(2.5, 1, 2), md: theme.spacing(3.25, 1, 2.75), xxl: theme.spacing(5, 1, 4.5) },
            borderRadius: `${theme.borderRadius.large}px`,
            transition: theme.transitions.create(['background-color', 'transform', 'box-shadow'], {
              duration: theme.transitions.duration.short,
            }),
            boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.15)',
            '@media (pointer: fine)': {
              '&:hover': {
                backgroundColor: theme.palette['gray ultra light'],
                transform: 'scale(1.04)',
                boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25), 0px 0px 20px rgba(0, 0, 0, 0.15)',
              },
            },
            ...sx,
          }}
        >
          <Icon
            sx={{
              fontSize: { xs: '3.25rem', [MOBILE_BREAKPOINT_THRESHOLD]: '3.75rem' },
              position: 'relative',
              zIndex: 1,
              color: disabled ? theme.palette['gray light'] : undefined,
            }}
          />
          <Typography
            sx={{
              mt: 0.5,
              typography: { xs: 'caption bold', [MOBILE_BREAKPOINT_THRESHOLD]: 'button' },
            }}
          >
            {capitalize(label)}
          </Typography>

          {catalogStats && <StatsLabel {...catalogStats} />}
        </StyledButton>
      )}
    </AnnounceHintWrapper>
  );
};
